/**
 * The readable foreground for an arbitrary tenant background.
 *
 * WHY THIS EXISTS. `getContrastingTextColor` from `@chatwoot/utils` decides with
 * the old YIQ formula and a fixed threshold:
 *
 *   r * 0.299 + g * 0.587 + b * 0.114 > 186 ? black : white
 *
 * YIQ brightness is not WCAG relative luminance, and a single threshold cannot
 * stand in for a ratio. On `#1f93ff` — Chatwoot's DEFAULT widget colour, so the
 * majority of the 46 inboxes — it returns white, and white on that blue measures
 * 3.15:1. Ink on the same blue measures 6.03:1. The helper picked the worse of
 * its own two options, on the customer's own message bubble.
 *
 * This keeps the shape of that decision — one of two fixed foregrounds, because
 * the value is written into an inline style and cannot follow the theme — but
 * makes it by measuring both against WCAG 2.1 relative luminance and returning
 * whichever actually wins.
 *
 * The dark option is the brand ink rather than pure black; on every colour where
 * the dark option wins at all it wins by a wide margin, so the small luminance
 * the ink adds never flips the outcome.
 */

const INK = '#000e30'; // --brand-ink
const PAPER = '#ffffff';

const HEX = /^#?([\da-f]{3}|[\da-f]{6})$/i;

/**
 * @param {string} hex '#rgb', '#rrggbb', or the same without the hash
 * @returns {number[]|null} [r, g, b] in 0-255, or null if it is not a colour
 */
export const parseHexColor = hex => {
  if (typeof hex !== 'string' || !HEX.test(hex.trim())) {
    return null;
  }
  let value = hex.trim().replace('#', '');
  if (value.length === 3) {
    value = value
      .split('')
      .map(char => char + char)
      .join('');
  }
  return [0, 2, 4].map(index => parseInt(value.slice(index, index + 2), 16));
};

const toLinear = channel => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/**
 * WCAG 2.1 relative luminance.
 * @param {number[]} rgb
 * @returns {number} 0 (black) to 1 (white)
 */
export const relativeLuminance = ([r, g, b]) =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

/**
 * WCAG 2.1 contrast ratio between two opaque colours.
 * @param {string} a hex colour
 * @param {string} b hex colour
 * @returns {number} 1 to 21
 */
export const contrastRatio = (a, b) => {
  const rgbA = parseHexColor(a);
  const rgbB = parseHexColor(b);
  if (!rgbA || !rgbB) {
    return 1;
  }
  const lumA = relativeLuminance(rgbA);
  const lumB = relativeLuminance(rgbB);
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
};

/**
 * @param {string} background the tenant's widget colour
 * @returns {string} the more readable of the brand's ink and paper. Falls back
 *   to paper for a missing or malformed colour, which is what the widget renders
 *   in the window between boot and the inbox config arriving.
 */
export const contrastingTextColor = background => {
  if (!parseHexColor(background)) {
    return PAPER;
  }
  return contrastRatio(INK, background) > contrastRatio(PAPER, background)
    ? INK
    : PAPER;
};

export { INK, PAPER };
